import { customerSchema } from '@alicerce/contracts';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  Dialog,
  EmptyState,
  formatDate,
  formatDateTime,
  formatDocument,
  formatPhone,
  Input,
  PageHeader,
  PageLoader,
  Table,
  Td,
  TextField,
  Th,
  Tr,
} from '@alicerce/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { LoadMore } from '@/components/load-more';
import { errorMessage } from '@/lib/api';
import { useZodForm } from '@/lib/form';
import { useCursorList, useDebounced } from '@/lib/hooks';
import { useApi, useCurrent } from '@/lib/session';

export interface Customer {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  document: string | null;
  tags: string[];
  marketingOptIn: boolean;
  source: string | null;
  anonymizedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface Address {
  id: string;
  zipCode: string;
  street: string;
  number: string;
  complement: string | null;
  district: string;
  city: string;
  state: string;
  isDefault: boolean;
}

interface CustomerDetail extends Customer {
  addresses: Address[];
  timeline: { at: string; moduleKey: string; type: string; title: string; link?: string }[];
}

const SOURCE_LABEL: Record<string, string> = { storefront: 'Loja', whatsapp: 'WhatsApp', manual: 'Cadastro manual', ads: 'Anúncio', webchat: 'Chat da loja', import: 'Importação' };

export function CustomersListPage() {
  const { can } = useCurrent();
  const [params, setParams] = useSearchParams();
  const search = params.get('q') ?? '';
  const tag = params.get('etiqueta') ?? '';
  const q = useDebounced(search);
  const list = useCursorList<Customer>(['customers'], '/v1/core/customers', { q: q || undefined, tag: tag || undefined, limit: 30 });
  const [creating, setCreating] = useState(false);

  return (
    <div>
      <PageHeader
        title="Clientes"
        description="Quem compra e conversa com você, com tudo o que já aconteceu."
        actions={can('customers:write') && <Button onClick={() => setCreating(true)}>Novo cliente</Button>}
      />
      <Card>
        <div className="flex flex-wrap gap-2 border-b border-line p-3">
          <Input
            aria-label="Buscar por nome, telefone ou e-mail"
            placeholder="Buscar por nome, telefone ou e-mail"
            className="max-w-sm"
            value={search}
            onChange={(e) => setParams((p) => (e.target.value ? p.set('q', e.target.value) : p.delete('q'), p), { replace: true })}
          />
          {tag && (
            <Button size="sm" variant="secondary" onClick={() => setParams((p) => (p.delete('etiqueta'), p))}>
              Etiqueta: {tag} ✕
            </Button>
          )}
        </div>
        {list.isPending ? (
          <PageLoader />
        ) : list.isError ? (
          <Alert tone="danger" className="m-3">
            {errorMessage(list.error)}
          </Alert>
        ) : list.items.length === 0 ? (
          <EmptyState title={q || tag ? 'Nenhum cliente encontrado' : 'Nenhum cliente ainda'} description="Clientes entram sozinhos pela loja, pelo WhatsApp e pela venda rápida." />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Nome</Th>
                  <Th>Telefone</Th>
                  <Th>E-mail</Th>
                  <Th>Etiquetas</Th>
                  <Th>Desde</Th>
                </tr>
              </thead>
              <tbody>
                {list.items.map((c) => (
                  <Tr key={c.id}>
                    <Td>
                      <Link to={`/clientes/${c.id}`} className="font-medium hover:underline">
                        {c.name}
                      </Link>
                    </Td>
                    <Td className="whitespace-nowrap">{formatPhone(c.phone)}</Td>
                    <Td>{c.email ?? '—'}</Td>
                    <Td>
                      <div className="flex flex-wrap gap-1">
                        {c.tags.map((t) => (
                          <button key={t} type="button" onClick={() => setParams((p) => (p.set('etiqueta', t), p))}>
                            <Badge tone="info">{t}</Badge>
                          </button>
                        ))}
                      </div>
                    </Td>
                    <Td className="whitespace-nowrap">{formatDate(c.createdAt)}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            <LoadMore hasNextPage={list.hasNextPage} isFetchingNextPage={list.isFetchingNextPage} fetchNextPage={list.fetchNextPage} />
          </>
        )}
      </Card>
      <CustomerDialog open={creating} onClose={() => setCreating(false)} />
    </div>
  );
}

function CustomerDialog({ open, onClose, customer }: { open: boolean; onClose: () => void; customer?: CustomerDetail }) {
  const api = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const form = useZodForm(customerSchema, {
    name: customer?.name ?? '',
    email: customer?.email ?? null,
    phone: customer?.phone ?? null,
    document: customer?.document ?? null,
    tags: customer?.tags ?? [],
    marketingOptIn: customer?.marketingOptIn ?? false,
  });
  const [tagsText, setTagsText] = useState((customer?.tags ?? []).join(', '));
  const onSubmit = form.submit(async (data) => {
    const body = { ...data, tags: tagsText.split(',').map((t) => t.trim()).filter(Boolean) };
    const saved = customer ? await api.patch<Customer>(`/v1/core/customers/${customer.id}`, body) : await api.post<Customer>('/v1/core/customers', body);
    await queryClient.invalidateQueries({ queryKey: ['customers'] });
    await queryClient.invalidateQueries({ queryKey: ['customer', saved.id] });
    onClose();
    if (!customer) navigate(`/clientes/${saved.id}`);
  });
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={customer ? 'Editar cliente' : 'Novo cliente'}
      description={customer ? undefined : 'Se já existir alguém com o mesmo telefone ou e-mail, usamos o cadastro existente.'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button loading={form.submitting} onClick={() => void onSubmit()}>
            Salvar
          </Button>
        </>
      }
    >
      <form className="space-y-3" onSubmit={onSubmit} noValidate>
        {form.formError && <Alert tone="danger">{form.formError}</Alert>}
        <TextField label="Nome" value={form.values.name} error={form.errors.name} onChange={(e) => form.set('name', e.target.value)} />
        <div className="grid gap-3 sm:grid-cols-2">
          <TextField label="Telefone (WhatsApp)" type="tel" value={form.values.phone ?? ''} error={form.errors.phone} onChange={(e) => form.set('phone', e.target.value || null)} />
          <TextField label="E-mail" type="email" value={form.values.email ?? ''} error={form.errors.email} onChange={(e) => form.set('email', e.target.value || null)} />
        </div>
        <TextField label="CPF ou CNPJ" inputMode="numeric" value={form.values.document ?? ''} error={form.errors.document} onChange={(e) => form.set('document', e.target.value || null)} />
        <TextField label="Etiquetas" hint="Separe por vírgula: vip, atacado" value={tagsText} onChange={(e) => setTagsText(e.target.value)} />
        <Checkbox label="Aceita receber promoções" checked={!!form.values.marketingOptIn} onChange={(e) => form.set('marketingOptIn', e.target.checked)} />
        <button type="submit" hidden />
      </form>
    </Dialog>
  );
}

export function CustomerDetailPage() {
  const { id = '' } = useParams();
  const api = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can } = useCurrent();
  const [editing, setEditing] = useState(false);
  const [confirmAnon, setConfirmAnon] = useState(false);
  const [merging, setMerging] = useState(false);
  const q = useQuery({ queryKey: ['customer', id], queryFn: () => api.get<CustomerDetail>(`/v1/core/customers/${id}`) });
  const anonymize = useMutation({
    mutationFn: () => api.post(`/v1/core/customers/${id}/anonymize`),
    onSuccess: async () => {
      setConfirmAnon(false);
      await queryClient.invalidateQueries({ queryKey: ['customer', id] });
      await queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });
  const exportData = useMutation({
    mutationFn: () => api.get<unknown>(`/v1/core/customers/${id}/export`),
    onSuccess: (data) => {
      const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `dados-cliente-${id}.json`;
      a.click();
      URL.revokeObjectURL(url);
    },
  });

  if (q.isPending) return <PageLoader />;
  if (q.isError) return <Alert tone="danger">{errorMessage(q.error)}</Alert>;
  const c = q.data;
  const anonymized = !!c.anonymizedAt;
  const editable = can('customers:write') && !anonymized;

  return (
    <div className="space-y-6">
      <PageHeader
        title={c.name}
        description={
          <span className="flex flex-wrap items-center gap-2">
            {c.source && <Badge>{SOURCE_LABEL[c.source] ?? c.source}</Badge>}
            {anonymized && <Badge tone="warning">Dados pessoais removidos em {formatDate(c.anonymizedAt)}</Badge>}
            <Link to="/clientes" className="text-brand-700 hover:underline">
              Voltar para clientes
            </Link>
          </span>
        }
        actions={
          editable && (
            <>
              <Button variant="secondary" onClick={() => setMerging(true)}>
                Unir duplicados
              </Button>
              <Button onClick={() => setEditing(true)}>Editar</Button>
            </>
          )
        }
      />
      <div className="grid gap-6 lg:grid-cols-[22rem_1fr]">
        <div className="space-y-6">
          <Card>
            <CardHeader title="Contato" />
            <CardBody>
              <dl className="space-y-2 text-sm">
                <div>
                  <dt className="text-muted">Telefone</dt>
                  <dd>{c.phone ? <a className="text-brand-700 hover:underline" href={`https://wa.me/${c.phone.replace(/\D/g, '')}`} target="_blank" rel="noreferrer">{formatPhone(c.phone)}</a> : '—'}</dd>
                </div>
                <div>
                  <dt className="text-muted">E-mail</dt>
                  <dd>{c.email ?? '—'}</dd>
                </div>
                <div>
                  <dt className="text-muted">CPF/CNPJ</dt>
                  <dd>{formatDocument(c.document)}</dd>
                </div>
                <div>
                  <dt className="text-muted">Promoções</dt>
                  <dd>{c.marketingOptIn ? 'Aceita receber' : 'Não aceita'}</dd>
                </div>
                {c.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-1">
                    {c.tags.map((t) => (
                      <Badge key={t} tone="info">
                        {t}
                      </Badge>
                    ))}
                  </div>
                )}
              </dl>
            </CardBody>
          </Card>
          <Card>
            <CardHeader title="Endereços" />
            <CardBody>
              {c.addresses.length === 0 ? (
                <p className="text-sm text-muted">Nenhum endereço.</p>
              ) : (
                <ul className="space-y-3 text-sm">
                  {c.addresses.map((a) => (
                    <li key={a.id}>
                      {a.street}, {a.number}
                      {a.complement ? ` – ${a.complement}` : ''}
                      <br />
                      {a.district}, {a.city}/{a.state} · CEP {a.zipCode.replace(/(\d{5})(\d{3})/, '$1-$2')}
                      {a.isDefault && (
                        <Badge className="ml-2" tone="neutral">
                          Principal
                        </Badge>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>
          <Card>
            <CardHeader title="Privacidade (LGPD)" />
            <CardBody className="space-y-3 text-sm">
              <p className="text-muted">O cliente pode pedir uma cópia dos dados ou que você apague os dados pessoais. Pedidos e notas ficam, como manda a lei.</p>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="secondary" loading={exportData.isPending} onClick={() => exportData.mutate()}>
                  Baixar dados (JSON)
                </Button>
                {editable && (
                  <Button size="sm" variant="danger" onClick={() => setConfirmAnon(true)}>
                    Apagar dados pessoais
                  </Button>
                )}
              </div>
              {exportData.isError && <Alert tone="danger">{errorMessage(exportData.error)}</Alert>}
            </CardBody>
          </Card>
        </div>
        <Card>
          <CardHeader title="Linha do tempo" description="Pedidos, conversas e notas deste cliente." />
          <CardBody>
            {c.timeline.length === 0 ? (
              <p className="text-sm text-muted">Nada por aqui ainda.</p>
            ) : (
              <ol className="relative space-y-4 border-l border-line pl-4">
                {c.timeline.map((e, i) => (
                  <li key={i} className="text-sm">
                    <span className="absolute -left-1.5 mt-1.5 size-3 rounded-full border-2 border-white bg-brand-500" aria-hidden />
                    <p className="text-xs text-muted">{formatDateTime(e.at)}</p>
                    {e.link ? (
                      <Link to={e.link} className="font-medium hover:underline">
                        {e.title}
                      </Link>
                    ) : (
                      <p className="font-medium">{e.title}</p>
                    )}
                  </li>
                ))}
              </ol>
            )}
          </CardBody>
        </Card>
      </div>
      {editing && <CustomerDialog open onClose={() => setEditing(false)} customer={c} />}
      <Dialog
        open={confirmAnon}
        onClose={() => setConfirmAnon(false)}
        title="Apagar os dados pessoais deste cliente?"
        description="Nome, telefone, e-mail, documento, endereços e o conteúdo das conversas serão removidos. Pedidos e notas ficam, sem os dados pessoais. Não dá para desfazer."
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmAnon(false)}>
              Cancelar
            </Button>
            <Button variant="danger" loading={anonymize.isPending} onClick={() => anonymize.mutate()}>
              Apagar dados
            </Button>
          </>
        }
      >
        {anonymize.isError && <Alert tone="danger">{errorMessage(anonymize.error)}</Alert>}
      </Dialog>
      {merging && (
        <MergeDialog
          into={c}
          onClose={() => setMerging(false)}
          onMerged={async () => {
            setMerging(false);
            await queryClient.invalidateQueries({ queryKey: ['customers'] });
            await queryClient.invalidateQueries({ queryKey: ['customer', id] });
            navigate(`/clientes/${id}`);
          }}
        />
      )}
    </div>
  );
}

function MergeDialog({ into, onClose, onMerged }: { into: Customer; onClose: () => void; onMerged: () => void }) {
  const api = useApi();
  const [search, setSearch] = useState(into.name);
  const q = useDebounced(search);
  const results = useCursorList<Customer>(['customers', 'merge'], '/v1/core/customers', { q: q || undefined, limit: 20 }, q.length >= 2);
  const [selected, setSelected] = useState<string[]>([]);
  const merge = useMutation({ mutationFn: () => api.post('/v1/core/customers/merge', { intoId: into.id, fromIds: selected }), onSuccess: onMerged });
  const candidates = results.items.filter((c) => c.id !== into.id && !c.anonymizedAt);
  return (
    <Dialog
      open
      onClose={onClose}
      title="Unir clientes duplicados"
      description={`Pedidos, conversas e endereços dos escolhidos passam para ${into.name}.`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button disabled={!selected.length} loading={merge.isPending} onClick={() => merge.mutate()}>
            Unir {selected.length || ''}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {merge.isError && <Alert tone="danger">{errorMessage(merge.error)}</Alert>}
        <Input aria-label="Buscar clientes" value={search} onChange={(e) => setSearch(e.target.value)} />
        {candidates.length === 0 ? (
          <p className="text-sm text-muted">Nenhum outro cliente encontrado.</p>
        ) : (
          <ul className="space-y-2">
            {candidates.map((c) => (
              <li key={c.id}>
                <Checkbox
                  label={`${c.name} · ${formatPhone(c.phone)} · ${c.email ?? 'sem e-mail'}`}
                  checked={selected.includes(c.id)}
                  onChange={(e) => setSelected(e.target.checked ? [...selected, c.id] : selected.filter((x) => x !== c.id))}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </Dialog>
  );
}
